import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BarChart3, Building2, Check, ChevronDown, RefreshCw, Table2 } from "lucide-react";
import useDaywiseByStationwiseGroup from "../hooks/useDaywiseByStationwiseGroup";
import useFetchAllStations from "../hooks/useFetchAllStations";
import { currentGroupId } from "../utils/access";
import { useWebSocketContext } from "../contexts/WebSocketProvider";
import { formatINR } from "../utils/formatINR";
import { addDays, formatDecimal, formatNumber, todayKey } from "../utils/analyticsMetrics";
import { dateGroups, formatHours, stationSeries, stationTotals, toRecords, totalsOf, trendSeries } from "../utils/stationHistory";
import { chartColors } from "../theme/palette";
import { Section, SegmentedControl } from "../components/Analytics/AnalyticsUI";
import KPICard, { Sparkline } from "../components/Analytics/KPICard";
import { MAX_COMPARE, RevenueEnergyTrends, SessionAnalytics, StationComparison } from "../components/StationHistory/HistoryCharts";
import { DateWiseReport, RecordsTable } from "../components/StationHistory/HistoryReports";

const PRESETS = [
  { value: "7d", label: "7 days", days: 7 },
  { value: "30d", label: "30 days", days: 30 },
  { value: "90d", label: "90 days", days: 90 },
  { value: "365d", label: "1 year", days: 365 },
  { value: "custom", label: "Custom" },
];
const GRANULARITY = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];
const VIEWS = [
  { value: "chart", label: <span className="inline-flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5" />Charts</span> },
  { value: "table", label: <span className="inline-flex items-center gap-1.5"><Table2 className="h-3.5 w-3.5" />Tables</span> },
];
const GRANULARITY_LABEL = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };

const presetRange = (preset) => {
  const days = PRESETS.find((p) => p.value === preset)?.days || 30;
  const to = todayKey();
  return { fromDate: addDays(to, -(days - 1)), toDate: to };
};

// multi-select of stations; an empty selection means all stations
const StationPicker = ({ options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  const label = !selected.length
    ? `All stations (${options.length})`
    : selected.length === 1
    ? options.find((o) => o.value === selected[0])?.label || selected[0]
    : `${selected.length} stations`;

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="input flex w-full min-w-[220px] items-center justify-between gap-2 text-left">
        <span className="flex min-w-0 items-center gap-2">
          <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" />
          <span className="truncate">{label}</span>
        </span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-neutral-400" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-line bg-white p-1 shadow-xl">
          <button
            type="button"
            onClick={() => onChange([])}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-13 font-medium text-fg hover:bg-neutral-50"
          >
            <span className={`flex h-4 w-4 items-center justify-center rounded ${!selected.length ? "bg-brand-600" : "ring-1 ring-inset ring-neutral-300"}`}>
              {!selected.length && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
            </span>
            All stations
          </button>
          <div className="my-1 h-px bg-line" />
          <div className="max-h-72 overflow-y-auto">
            {options.map((o) => {
              const on = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-13 text-neutral-700 hover:bg-neutral-50"
                >
                  <span className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded ${on ? "bg-brand-600" : "ring-1 ring-inset ring-neutral-300"}`}>
                    {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  {o.label !== o.value && <span className="flex-shrink-0 font-mono text-[10px] text-neutral-400">{o.value}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default function DaywiseStationGroup({ stationIds = null }) {
  const [preset, setPreset] = useState("30d");
  const [customRange, setCustomRange] = useState(() => presetRange("30d"));
  const [granularity, setGranularity] = useState("daily");
  const [view, setView] = useState("chart");
  const [selectedStations, setSelectedStations] = useState([]);
  const [metric, setMetric] = useState("revenue");

  const { data, isLoading, error, refetch } = useDaywiseByStationwiseGroup(customRange, stationIds);
  const { stations } = useFetchAllStations({ groupId: currentGroupId() });
  const { isConnected } = useWebSocketContext();

  // the hook starts idle (and skips its request while offline); show skeletons
  // until a request made while connected has finished
  const [loadedOnce, setLoadedOnce] = useState(false);
  const sawLoading = useRef(false);
  useEffect(() => {
    if (isLoading && isConnected) sawLoading.current = true;
    else if (!isLoading && sawLoading.current) setLoadedOnce(true);
  }, [isLoading, isConnected]);
  const loading = !loadedOnce || (isLoading && !data?.length);

  const names = useMemo(() => Object.fromEntries((stations || []).map((s) => [String(s.stationId), s.stationName])), [stations]);
  const nameOf = useCallback((id) => names[id] || id, [names]);

  const allRecords = useMemo(() => toRecords(data), [data]);
  const records = useMemo(
    () => (selectedStations.length ? allRecords.filter((r) => selectedStations.includes(r.stationId)) : allRecords),
    [allRecords, selectedStations]
  );

  const stationOptions = useMemo(() => {
    const ids = new Set([...allRecords.map((r) => r.stationId), ...Object.keys(names)]);
    return [...ids]
      .map((id) => ({ value: id, label: nameOf(id) }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [allRecords, names, nameOf]);

  const totals = useMemo(() => totalsOf(records), [records]);
  const trend = useMemo(() => trendSeries(records, customRange, granularity), [records, customRange, granularity]);
  const perStation = useMemo(() => stationTotals(records), [records]);
  const groups = useMemo(() => dateGroups(records), [records]);

  // ---- station comparison: each compared station keeps its color slot until removed ----
  const [slots, setSlots] = useState({});
  const touched = useRef(false);
  const compare = useMemo(
    () => Object.keys(slots).filter((id) => perStation.some((t) => t.stationId === id)).sort((a, b) => slots[a] - slots[b]),
    [slots, perStation]
  );
  useEffect(() => {
    if (touched.current || Object.keys(slots).length || !perStation.length) return;
    setSlots(Object.fromEntries(perStation.slice(0, 3).map((t, i) => [t.stationId, i])));
  }, [perStation, slots]);
  const toggleCompare = (id) => {
    touched.current = true;
    setSlots((prev) => {
      if (id in prev) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      const used = new Set(Object.values(prev));
      const free = chartColors.categorical.findIndex((_, i) => !used.has(i));
      if (free === -1 || used.size >= MAX_COMPARE) return prev;
      return { ...prev, [id]: free };
    });
  };
  const colorOf = useCallback((id) => chartColors.categorical[slots[id]] || chartColors.axis, [slots]);
  const metricKey = { revenue: "final", energy: "energy", sessions: "sessions", duration: "duration" }[metric];
  const comparison = useMemo(
    () => stationSeries(records, customRange, granularity, compare, metricKey),
    [records, customRange, granularity, compare, metricKey]
  );

  const onPreset = (p) => {
    setPreset(p);
    if (p !== "custom") setCustomRange(presetRange(p));
  };
  const setCustom = (next) => {
    if (next.fromDate && next.toDate && next.fromDate <= next.toDate) setCustomRange(next);
  };

  const unknownDays = records.filter((r) => r.date === "unknown").length;
  const gLabel = GRANULARITY_LABEL[granularity];

  return (
    <div className="mx-auto max-w-[1600px] space-y-8 pb-12">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-brand-950">Station History</h1>
        <p className="page-subtitle">Daily performance of every station: revenue, energy, sessions and charging time over any period.</p>
      </div>

      {/* 1. controls */}
      <div className="card z-20 flex xl:sticky xl:top-0 flex-col gap-4 p-4 xl:flex-row xl:flex-wrap xl:items-end">
        <div className="flex flex-col gap-1.5">
          <span className="field-label">Period</span>
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl options={PRESETS} value={preset} onChange={onPreset} size="md" />
            {preset === "custom" ? (
              <div className="flex items-center gap-2">
                <input type="date" value={customRange.fromDate} max={customRange.toDate} onChange={(e) => setCustom({ ...customRange, fromDate: e.target.value })} className="input w-[150px]" aria-label="From date" />
                <span className="text-13 text-muted">to</span>
                <input type="date" value={customRange.toDate} min={customRange.fromDate} onChange={(e) => setCustom({ ...customRange, toDate: e.target.value })} className="input w-[150px]" aria-label="To date" />
              </div>
            ) : (
              <span className="text-13 text-muted">
                <span className="font-medium tabular-nums text-fg">{customRange.fromDate}</span> → <span className="font-medium tabular-nums text-fg">{customRange.toDate}</span>
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 xl:ml-auto">
          <label className="flex flex-col gap-1.5">
            <span className="field-label">Stations</span>
            <StationPicker options={stationOptions} selected={selectedStations} onChange={setSelectedStations} />
          </label>
          <div className="flex flex-col gap-1.5">
            <span className="field-label">Group by</span>
            <SegmentedControl options={GRANULARITY} value={granularity} onChange={setGranularity} size="md" />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="field-label">View</span>
            <SegmentedControl options={VIEWS} value={view} onChange={setView} size="md" />
          </div>
          <button type="button" onClick={() => refetch()} disabled={isLoading} className="btn-secondary">
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-13 text-red-700">
          {data?.length ? "Showing the last loaded data. " : ""}Couldn't load station history: {error}. Retrying automatically.
        </p>
      )}

      {/* 2. KPIs */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <KPICard
          label="Final revenue"
          value={formatINR(totals.final)}
          hint={`Base ${formatINR(totals.base)} + GST ${formatINR(totals.gst)}`}
          isLoading={loading}
          spark={<Sparkline data={trend.map((d) => ({ ...d, day: d.label }))} dataKey="final" formatter={formatINR} />}
        />
        <KPICard
          label="Energy delivered"
          value={formatDecimal(totals.energy)}
          unit="kWh"
          isLoading={loading}
          spark={<Sparkline data={trend.map((d) => ({ ...d, day: d.label }))} dataKey="energy" color={chartColors.secondary} formatter={(v) => `${formatDecimal(v)} kWh`} />}
        />
        <KPICard
          label="Charging sessions"
          value={formatNumber(totals.sessions)}
          isLoading={loading}
          spark={<Sparkline data={trend.map((d) => ({ ...d, day: d.label }))} dataKey="sessions" formatter={formatNumber} />}
        />
        <KPICard
          label="Charging time"
          value={formatHours(totals.duration)}
          isLoading={loading}
          spark={<Sparkline data={trend.map((d) => ({ ...d, day: d.label }))} dataKey="duration" color={chartColors.secondary} formatter={formatHours} />}
        />
        <KPICard
          label="Active stations"
          value={formatNumber(totals.stations)}
          hint={`${formatNumber(groups.filter((g) => g.date !== "unknown").length)} days with sessions`}
          isLoading={loading}
        />
      </section>
      {unknownDays > 0 && !loading && (
        <p className="-mt-4 text-xs text-muted">
          {formatNumber(unknownDays)} records have no date; they are included in totals and tables but not in the time charts.
        </p>
      )}

      {/* 3. trends */}
      <Section title="Revenue and energy trends" description="How much was earned and delivered over the period.">
        <RevenueEnergyTrends series={trend} view={view} isLoading={loading} granularityLabel={gLabel} />
      </Section>

      {/* 4. station comparison */}
      <Section title="Station comparison" description="Compare stations side by side for revenue, energy, sessions or charging time.">
        <StationComparison
          series={comparison}
          totals={perStation}
          compare={compare}
          colorOf={colorOf}
          onToggle={toggleCompare}
          nameOf={nameOf}
          metric={metric}
          onMetricChange={setMetric}
          view={view}
          isLoading={loading}
          granularityLabel={gLabel}
        />
      </Section>

      {/* 5. session analytics */}
      <Section title="Charging sessions" description="Session volume and time spent charging.">
        <SessionAnalytics series={trend} totals={totals} view={view} isLoading={loading} granularityLabel={gLabel} />
      </Section>

      {/* 6. date-wise report */}
      <Section title="Date-wise report" description="Every day's totals, with each station's figures underneath.">
        <DateWiseReport groups={groups} nameOf={nameOf} isLoading={loading} />
      </Section>

      {/* 7. detailed records */}
      <Section title="Historical records" description="Every station-day record for the selected period and stations.">
        <RecordsTable records={records} totals={totals} nameOf={nameOf} isLoading={loading} range={customRange} />
      </Section>
    </div>
  );
}
